import type { AxiosProgressEvent, AxiosRequestConfig } from 'axios'

import type {
  AutoFillMessageType,
  CodableTrustRegistration,
  TrustRegistrationObject,
  /* eslint-disable-next-line import-x/no-restricted-paths -- FIXME: UXP-4500 */
} from '@src/app/settings/trust/trustTypes'
import config from '@src/config'
import type {
  TollFreeRegistrationCreateResponse,
  TollFreeRegistrationGetResponse,
  TollFreeRegistrationLiteGetResponse,
  TollFreeRegistrationSubmitResponse,
  TollFreeRegistrationUpdateResponse,
  TrustCnamCreateRequest,
  TrustCnamCreateResponse,
  TrustCnamDeleteRequest,
  TrustCnamDeleteResponse,
  TrustCnamGetAllResponse,
  TrustCnamUpdateRequest,
  TrustCnamUpdateResponse,
  TrustRegistration3DSVerificationResponse,
  TrustRegistrationV2CreateRequest,
  TrustRegistrationV2CreateResponse,
  TrustRegistrationV2GetResponse,
  TrustRegistrationV2LiteGetResponse,
  TrustRegistrationV2UpdateRequest,
  TrustRegistrationV2UpdateResponse,
} from '@src/service/dto'
import type TollFreeRegistrationAutofillRequest from '@src/service/dto/request/toll-free-registration/TollFreeRegistrationAutofillRequest'
import type TollFreeRegistrationCreateRequest from '@src/service/dto/request/toll-free-registration/TollFreeRegistrationCreateRequest'
import type TollFreeRegistrationSubmitRequest from '@src/service/dto/request/toll-free-registration/TollFreeRegistrationSubmitRequest'
import type TollFreeRegistrationUpdateRequest from '@src/service/dto/request/toll-free-registration/TollFreeRegistrationUpdateRequest'

import type Transport from '.'
import { HttpTransaction } from './transaction'

export default class TrustClient {
  constructor(private readonly transport: Transport) {}

  cnam = {
    getAll: (): Promise<TrustCnamGetAllResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}cnam`,
          query: {
            // FIXME: https://linear.app/openphone/issue/ENG-4182
            limit: 1000,
          },
        }),
      )
    },
    create: (dto: TrustCnamCreateRequest): Promise<TrustCnamCreateResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}cnam`,
          method: 'POST',
          body: dto,
        }),
      )
    },
    delete: (dto: TrustCnamDeleteRequest): Promise<TrustCnamDeleteResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}cnam/${dto.cnamId}`,
          method: 'DELETE',
          headers: {
            'If-Match': dto.etag,
          },
        }),
      )
    },
    update: (dto: TrustCnamUpdateRequest): Promise<TrustCnamUpdateResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}cnam/${dto.cnamId}`,
          method: 'PUT',
          body: {
            displayName: dto.displayName,
            useAsDefault: dto.useAsDefault,
            resourceIds: dto.resourceIds,
          },
          headers: {
            'If-Match': dto.etag,
          },
        }),
      )
    },
  }

  registration = {
    fetch: (): Promise<CodableTrustRegistration> => {
      return this.transport.queue(
        new HttpTransaction({ url: `${config.TRUST_SERVICE_URL}registration` }),
      )
    },
    create: (
      registration: Partial<TrustRegistrationObject>,
    ): Promise<CodableTrustRegistration> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}registration`,
          method: 'POST',
          body: registration,
        }),
      )
    },
    update: (
      id: string,
      etag: string,
      registration: Partial<TrustRegistrationObject>,
    ): Promise<CodableTrustRegistration> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}registration/${id}`,
          method: 'PUT',
          body: registration,
          headers: {
            'If-Match': etag,
          },
        }),
      )
    },
    delete: (id: string, etag: string): Promise<object> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}registration/${id}`,
          method: 'DELETE',
          headers: {
            'If-Match': etag,
          },
        }),
      )
    },
  }

  registrationV2 = {
    fetch: (): Promise<TrustRegistrationV2GetResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}registration`,
        }),
      )
    },
    fetchLite: (): Promise<TrustRegistrationV2LiteGetResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}registration/lite`,
        }),
      )
    },
    create: (
      dto: TrustRegistrationV2CreateRequest,
    ): Promise<TrustRegistrationV2CreateResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}registration`,
          method: 'POST',
          body: dto,
        }),
      )
    },
    update: ({
      etag,
      id,
      ...fields
    }: TrustRegistrationV2UpdateRequest): Promise<TrustRegistrationV2UpdateResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}registration/${id}`,
          method: 'PUT',
          body: fields,
          headers: {
            'If-Match': etag,
          },
        }),
      )
    },
    pay: (): Promise<TrustRegistration3DSVerificationResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}registration/pay`,
          method: 'POST',
        }),
      )
    },
    brandSms: (registrationId: string): Promise<void> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}registration/${registrationId}/brandSms`,
          method: 'POST',
        }),
      )
    },
    autofill: (
      registrationId: string,
      etag: string,
      dto: { consentMethod: 'verbal'; messageTypes: AutoFillMessageType[] },
    ): Promise<TrustRegistrationV2UpdateResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}registration/${registrationId}/autofill`,
          method: 'POST',
          body: dto,
          headers: {
            'If-Match': etag,
          },
        }),
      )
    },
  }

  tollFreeRegistration = {
    fetch: (): Promise<TollFreeRegistrationGetResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}tfn-registrations`,
        }),
      )
    },
    // Uploads an opt-in proof image to the private trust bucket and returns the persisted object key
    // (not a URL). The key is what we store on the registration's `optInImageUrls`.
    uploadOptInImage: async (
      file: File,
      onProgress?: (loaded: number, total: number) => void,
    ): Promise<string> => {
      const { url, key } = await this.transport.queue<{ url: string; key: string }>(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}tfn-registrations/upload-url`,
          method: 'POST',
          body: { type: file.type },
        }),
      )

      const requestConfig: AxiosRequestConfig = {
        headers: {
          'Content-Type': file.type,
        },
        onUploadProgress: (progress: AxiosProgressEvent) => {
          if (onProgress && typeof progress.total === 'number') {
            onProgress(progress.loaded, progress.total)
          }
        },
      }

      await this.transport.client.put(url, file, requestConfig)

      return key
    },
    fetchLite: (): Promise<TollFreeRegistrationLiteGetResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}tfn-registrations/lite`,
        }),
      )
    },
    create: (
      dto: TollFreeRegistrationCreateRequest,
    ): Promise<TollFreeRegistrationCreateResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}tfn-registrations`,
          method: 'POST',
          body: dto,
        }),
      )
    },
    update: ({
      id,
      ...fields
    }: TollFreeRegistrationUpdateRequest): Promise<TollFreeRegistrationUpdateResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}tfn-registrations/${id}`,
          method: 'PATCH',
          body: fields,
        }),
      )
    },
    submit: ({
      id,
      ...fields
    }: TollFreeRegistrationSubmitRequest): Promise<TollFreeRegistrationSubmitResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}tfn-registrations/${id}`,
          method: 'PUT',
          body: fields,
        }),
      )
    },
    autofill: ({
      id,
      useCaseCategories,
    }: TollFreeRegistrationAutofillRequest): Promise<TollFreeRegistrationUpdateResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}tfn-registrations/${id}/autofill`,
          method: 'POST',
          body: { useCaseCategories },
        }),
      )
    },
    delete: (id: string): Promise<object> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.TRUST_SERVICE_URL}tfn-registrations/${id}`,
          method: 'DELETE',
        }),
      )
    },
  }
}
